import { useEffect, useState } from 'react';
import { useTheme } from '../hooks/useTheme.js';
import { WORLD } from '../lib/world.js';

type Health = { ok: boolean; service?: string; time?: string } | null;

export function Hello(): JSX.Element {
  const [health, setHealth] = useState<Health>(null);
  const [err, setErr] = useState<string | null>(null);
  // Користуємось глобальною темою (не пишемо body.dataset напряму, щоб не десинкати).
  const { theme, cycle: setTheme } = useTheme();

  useEffect(() => {
    fetch('/api/health')
      .then((r) => r.json())
      .then(setHealth)
      .catch((e) => setErr(String(e)));
  }, []);

  const tokenStyle = (cssVar: string): React.CSSProperties => ({
    background: `var(${cssVar})`,
    width: 48,
    height: 48,
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 2px 4px rgba(0,0,0,0.3)',
  });

  const swatches: Array<[string, string]> = [
    ['--gold', 'gold'],
    ['--gold-bright', 'gold-bright'],
    ['--verdigris', 'verdigris'],
    ['--blood', 'blood'],
    ['--corruption', 'corruption'],
    ['--rot', 'rot'],
    ['--ice', 'ice'],
    ['--text', 'text'],
  ];

  return (
    <div
      className="app-shell"
      style={{
        padding: '64px 32px',
        fontFamily: theme === 'dark' ? 'Spectral, serif' : 'Spectral, serif',
      }}
    >
      <header style={{ maxWidth: 900, margin: '0 auto 32px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 24,
          }}
        >
          <h1
            style={{
              fontFamily: theme === 'dark' ? 'Cormorant Garamond, serif' : 'Cormorant Garamond, serif',
              fontSize: 48,
              margin: 0,
              color: theme === 'dark' ? 'var(--gold-bright)' : 'var(--dh-ink)',
              letterSpacing: '-0.005em',
              fontWeight: 600,
            }}
          >
            {WORLD.brandName}
          </h1>
          <button
            type="button"
            onClick={() => setTheme()}
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--text-muted)',
              padding: '8px 14px',
              borderRadius: 4,
              fontFamily: 'Spline Sans Mono, monospace',
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              cursor: 'pointer',
            }}
          >
            {theme === 'dark' ? '☾ Темна' : '☀ Світла'}
          </button>
        </div>
        <p
          style={{
            color: theme === 'dark' ? 'var(--text-muted)' : 'var(--dh-ink-muted)',
            fontStyle: 'italic',
            marginTop: 8,
            fontFamily: 'Spectral, serif',
          }}
        >
          Daggerheart — Ф0 каркас живий.
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 12,
            margin: '24px 0',
            color: 'var(--gold-dim)',
            fontSize: 20,
          }}
        >
          <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, var(--gold-dim))' }} />
          <span>✦</span>
          <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, var(--gold-dim), transparent)' }} />
        </div>
      </header>

      <section style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gap: 32 }}>
        <div
          style={{
            padding: 24,
            background: theme === 'dark' ? 'var(--bg-card)' : 'var(--dh-paper)',
            border: `1px solid ${theme === 'dark' ? 'var(--border)' : 'var(--dh-border)'}`,
            borderRadius: 6,
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <h2
            style={{
              fontFamily: theme === 'dark' ? 'Cormorant Garamond, serif' : 'Cormorant Garamond, serif',
              fontSize: 14,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              color: theme === 'dark' ? 'var(--gold)' : 'var(--dh-ink-muted)',
              margin: '0 0 12px',
            }}
          >
            Статус бекенду
          </h2>
          {health ? (
            <pre
              style={{
                margin: 0,
                fontFamily: 'Spline Sans Mono, monospace',
                fontSize: 13,
                color: 'var(--text)',
                background: 'rgba(0,0,0,0.25)',
                padding: 12,
                borderRadius: 4,
                overflow: 'auto',
              }}
            >
              {JSON.stringify(health, null, 2)}
            </pre>
          ) : err ? (
            <p style={{ color: 'var(--blood-bright)', fontFamily: 'Spline Sans Mono, monospace', fontSize: 13 }}>
              ⚠ {err}
            </p>
          ) : (
            <p style={{ color: 'var(--text-dim)' }}>пінгую /api/health…</p>
          )}
        </div>

        <div
          style={{
            padding: 24,
            background: theme === 'dark' ? 'var(--bg-card)' : 'var(--dh-paper)',
            border: `1px solid ${theme === 'dark' ? 'var(--border)' : 'var(--dh-border)'}`,
            borderRadius: 6,
          }}
        >
          <h2
            style={{
              fontFamily: theme === 'dark' ? 'Cormorant Garamond, serif' : 'Cormorant Garamond, serif',
              fontSize: 14,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              color: theme === 'dark' ? 'var(--gold)' : 'var(--dh-ink-muted)',
              margin: '0 0 16px',
            }}
          >
            Палітра
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 12 }}>
            {swatches.map(([cssVar, label]) => (
              <div key={cssVar} style={{ textAlign: 'center' }}>
                <div style={tokenStyle(cssVar)} />
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 10,
                    fontFamily: 'Spline Sans Mono, monospace',
                    color: 'var(--text-dim)',
                  }}
                >
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <footer
          style={{
            marginTop: 16,
            color: 'var(--text-dim)',
            fontSize: 12,
            fontFamily: 'Spline Sans Mono, monospace',
            textAlign: 'center',
          }}
        >
          ф0 · каркас · далі: Ф1 вікі
        </footer>
      </section>
    </div>
  );
}
